import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch } from 'react-redux';
import { Loader, useStepper } from '@monorepo/common';

import { FinalStep } from './FinalStep';
import { StepperInfoPanel } from './StepperInfoPanel';

import {
  staticValuesForStep2,
  staticValuesForStep3,
  staticValuesForStep1,
  staticValuesForStep4,
  staticValuesForStep5, staticValuesForStep6
} from './staticValues';

import { IStep1Form } from './Step1/formInterface';
import { IStep2Form } from './Step2/formInterfaces';
import { IStep3Form } from './Step3/formInterface';
import { IStep4Form } from './Step4/formInterface';
import { IStep5Form } from './Step5/formInterfaces';
import { IStep6Form } from './Step6/formInterfaces';

import { Step1 } from './Step1';
import { Step2 } from './Step2';
import { Step3 } from './Step3';
import { Step4 } from './Step4';
import { Step5 } from './Step5';
import { Step6 } from './Step6';
import { IEditJobData } from '../interfaces';
import { formattedLocations } from '../utils/formattedlocation';
import { useEditJobMutation } from '../../../services/serviceJobData';
import { showNotification } from '../../../features/sliceNotification';
import { ACCESS_REMOVED_MESSAGE, isForbidden } from '../../../utils/jobAccessError';
import { getMutationErrorMessage } from '../../../utils/mutationErrorMessage';
import {
  buildEditJobBody,
  IEditableJobSteps,
} from '../utils/buildEditJobBody';
import { LeavePageGuard, UNSAVED_EDIT_MESSAGE } from '../../../components/LeavePageGuard';
import { getShortDescriptionError, replaceLineBreaks } from '../../../utils/shortDescription';
import { hasEditJobChanges, isSameJobDescription } from '../utils/hasEditJobChanges';

// Step index of Job Description, where the Short Description lives.
const JOB_DESCRIPTION_STEP = 2;
type StepCommits = Record<number, () => void>;
const snapshot = <T,>(value: T): T => JSON.parse(JSON.stringify(value)) as T;

interface IStepperPanelProps {
  data: IEditJobData;
  guid: string;
}

export const StepperPanel = ({ data, guid }:IStepperPanelProps) => {
  const dispatch = useDispatch();
  const [editJob, { isLoading }] = useEditJobMutation();
  const [step1, setStep1] = useState<IStep1Form>(staticValuesForStep1);
  const [step2, setStep2] = useState<IStep2Form>(staticValuesForStep2);
  const [step3, setStep3] = useState<IStep3Form>(staticValuesForStep3);
  const [step4, setStep4] = useState<IStep4Form>(staticValuesForStep4);
  const [step5, setStep5] = useState<IStep5Form>(staticValuesForStep5);
  const [step6, setStep6] = useState<IStep6Form>(staticValuesForStep6);
  const [completedEdit, setCompletedEdit] = useState<boolean>(false);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState<boolean>(false);
  // Committed step state changes on Next, Back, Save or a step header. Keep the active form's
  // live values here too, so leaving directly from a step sees its unsaved edits.
  const savedSteps = useRef<IEditableJobSteps | null>(null);
  const draftSteps = useRef<IEditableJobSteps | null>(null);
  // Commit callbacks update this synchronously. A newly mounted step can submit before
  // React republishes the stepper's rendered button handlers with fresh state.
  const committedSteps = useRef<IEditableJobSteps | null>(null);
  const loadedGuid = useRef<string | null>(null);
  const pendingLogoUploads = useRef(0);
  const stepCommits = useRef<StepCommits>({});
  const registerStepCommit = useCallback((step: number, commitNow: () => void) => {
    stepCommits.current[step] = commitNow;
    return () => {
      if (stepCommits.current[step] === commitNow) delete stepCommits.current[step];
    };
  }, []);
  const commitBeforeStepChange = useCallback((step: number) => {
    stepCommits.current[step]?.();
  }, []);
  const updateDraft = useCallback((change: Partial<IEditableJobSteps>) => {
    if (!draftSteps.current || !savedSteps.current) return;
    draftSteps.current = { ...draftSteps.current, ...change };
    setHasUnsavedChanges(pendingLogoUploads.current > 0
      || hasEditJobChanges(draftSteps.current, savedSteps.current));
  }, []);
  const normalizeEditorDescription = useCallback((given: string, description: string) => {
    if (!draftSteps.current || !savedSteps.current) return;
    const draft = draftSteps.current;
    const saved = savedSteps.current;
    // Quill rewrote HTML it was given (the saved description, in one or two passes as it
    // loads). That rewrite is not an edit. When Quill was given the saved description, or
    // an earlier rewrite of it, the rewrite becomes the save baseline; when it was given
    // something the user typed, the original baseline stays. Judge by what Quill was given,
    // not the draft: the form echoes intermediate values into the draft while it loads.
    if (isSameJobDescription(given, saved.step3.jobDescription)) {
      savedSteps.current = {
        ...saved,
        step3: { ...saved.step3, jobDescription: description }
      };
    }
    draftSteps.current = {
      ...draft,
      step3: { ...draft.step3, jobDescription: description }
    };
    setHasUnsavedChanges(pendingLogoUploads.current > 0
      || hasEditJobChanges(draftSteps.current, savedSteps.current));
  }, []);
  const setLogoUploadPending = useCallback((pending: boolean) => {
    pendingLogoUploads.current = Math.max(0, pendingLogoUploads.current + (pending ? 1 : -1));
    if (draftSteps.current && savedSteps.current) {
      setHasUnsavedChanges(pendingLogoUploads.current > 0
        || hasEditJobChanges(draftSteps.current, savedSteps.current));
    }
  }, []);
  // A step also commits when it unmounts (see useCommitOnLeave). When the whole panel is
  // unmounting (leaving the page, another listing), that commit has nowhere to go.
  const isMounted = useRef(true);
  useEffect(() => () => {
    isMounted.current = false;
  }, []);
  const commitStep1 = useCallback((value: IStep1Form) => {
    if (!isMounted.current) return;
    if (committedSteps.current) committedSteps.current = { ...committedSteps.current, step1: snapshot(value) };
    setStep1(value);
    updateDraft({ step1: value });
  }, [updateDraft]);
  const commitStep2 = useCallback((value: IStep2Form) => {
    if (!isMounted.current) return;
    if (committedSteps.current) committedSteps.current = { ...committedSteps.current, step2: snapshot(value) };
    setStep2(value);
    updateDraft({ step2: value });
  }, [updateDraft]);
  const commitStep3 = useCallback((value: IStep3Form) => {
    if (!isMounted.current) return;
    if (committedSteps.current) committedSteps.current = { ...committedSteps.current, step3: snapshot(value) };
    setStep3(value);
    updateDraft({ step3: value });
  }, [updateDraft]);
  const commitStep4 = useCallback((value: IStep4Form) => {
    if (!isMounted.current) return;
    if (committedSteps.current) committedSteps.current = { ...committedSteps.current, step4: snapshot(value) };
    setStep4(value);
    updateDraft({ step4: value });
  }, [updateDraft]);
  const commitStep5 = useCallback((value: IStep5Form) => {
    if (!isMounted.current) return;
    if (committedSteps.current) committedSteps.current = { ...committedSteps.current, step5: snapshot(value) };
    setStep5(value);
    updateDraft({ step5: value });
  }, [updateDraft]);

  const { putSteps, onNext, onBack, onComplete, renderStepper, StepsButtons, goToStep, resetActiveStep } = useStepper({
    orientation: 'vertical',
    isHideLeftInfoPanelAfterComplete: true,
    leftInfoPanel: (
      <StepperInfoPanel
        data={data}
        guid={guid}
        hasUnsavedChanges={hasUnsavedChanges}
      />
    ),
    finalComponent: <FinalStep />,
    isLoading,
    onBeforeStepChange: commitBeforeStepChange
  });
  // The steps are published as a snapshot (putSteps), so submitJob reads goToStep through a ref
  // rather than a closure that could hold an old list of reachable steps.
  const goToStepRef = useRef(goToStep);
  goToStepRef.current = goToStep;

  const submitJob = useCallback(async (
    mode: 'save' | 'complete',
    overrides: Partial<IEditableJobSteps> = {}
  ) => {
    const body = JSON.parse(JSON.stringify(buildEditJobBody(committedSteps.current || {
      step1,
      step2,
      step3,
      step4,
      step5,
    }, overrides))) as IEditableJobSteps;

    // Every save sends the Short Description, even from a step that never showed it, so check
    // it first: a listing saved before the current rule (an email address in it, say) would
    // otherwise fail with a server error about a field the user never opened. The Job
    // Description step checks its own form, so this only matters from the other steps.
    const shortDescriptionError = getShortDescriptionError(body.step3.shortDescription);
    if (shortDescriptionError) {
      dispatch(showNotification({
        show: true,
        type: 'error',
        text: `${shortDescriptionError} You can change it on the Job Description step.`
      }));
      // Only moves if that step has been opened; the message is enough otherwise.
      goToStepRef.current(JOB_DESCRIPTION_STEP);
      return;
    }

    const result = await editJob({
      id: guid,
      body
    });
    if ('error' in result) {
      dispatch(showNotification({
        show: true,
        type: 'error',
        text: isForbidden(result.error)
          ? ACCESS_REMOVED_MESSAGE
          : getMutationErrorMessage(
            result.error,
            'We could not save your job listing. Please try again.'
          )
      }));
      return;
    }

    if ('data' in result) {
      // A later edit may have happened while this request was in flight. Compare
      // its current value with the exact body that the API just accepted.
      savedSteps.current = body;
      if (draftSteps.current) {
        setHasUnsavedChanges(pendingLogoUploads.current > 0
          || hasEditJobChanges(draftSteps.current, body));
      }
      if (mode === 'complete') {
        setCompletedEdit(true);
      } else {
        dispatch(showNotification({
          show: true,
          type: 'success',
          text: 'Job listing saved.'
        }));
      }
    }
  }, [dispatch, editJob, guid, step1, step2, step3, step4, step5]);

  const finalRequest = useCallback(() => {
    submitJob('complete');
  }, [submitJob]);

  const steps = useMemo(() => [
    {
      label: 'Basic Job Details',
      content: (
        <Step1
          setStep1={commitStep1}
          onDraftChange={(value) => updateDraft({ step1: value })}
          registerCommit={(commitNow) => registerStepCommit(0, commitNow)}
          defaultData={step1}
          Nav={StepsButtons}
          onNext={onNext}
          onSave={(value) => submitJob('save', { step1: value })}
        />
      )
    },
    {
      label: 'Compensation & Location',
      content: (
        <Step2
          setStep2={commitStep2}
          onDraftChange={(value) => updateDraft({ step2: value })}
          registerCommit={(commitNow) => registerStepCommit(1, commitNow)}
          defaultData={step2}
          Nav={StepsButtons}
          onNext={onNext}
          onBack={onBack}
          onSave={(value) => submitJob('save', { step2: value })}
        />
      )
    },
    {
      label: 'Job Description',
      content: (
        <Step3
          setStep3={commitStep3}
          onDraftChange={(value) => updateDraft({ step3: value })}
          onEditorNormalize={normalizeEditorDescription}
          registerCommit={(commitNow) => registerStepCommit(2, commitNow)}
          defaultData={step3}
          Nav={StepsButtons}
          onNext={onNext}
          onBack={onBack}
          onSave={(value) => submitJob('save', { step3: value })}
        />
      )
    },
    {
      label: 'Upload Logo',
      content: (
        <Step4
          setStep4={(value) => {
            // An upload from the previous listing may finish after a guid change.
            if (loadedGuid.current === guid) commitStep4(value);
          }}
          onUploadPendingChange={(pending) => {
            if (loadedGuid.current === guid) setLogoUploadPending(pending);
          }}
          defaultData={step4}
          Nav={StepsButtons}
          onNext={onNext}
          onBack={onBack}
          onSave={(value) => submitJob('save', { step4: value })}
        />
      )
    },
    {
      label: 'Contact and Application Details',
      content: (
        <Step5
          defaultValue={step5}
          setStep5={commitStep5}
          onDraftChange={(value) => updateDraft({ step5: value })}
          registerCommit={(commitNow) => registerStepCommit(4, commitNow)}
          Nav={StepsButtons}
          onNext={onNext}
          onBack={onBack}
          onSave={(value) => submitJob('save', { step5: value })}
        />
      )
    },
    {
      label: 'Terms and Conditions',
      content: (
        <Step6
          isEdited={completedEdit}
          Nav={StepsButtons}
          onNext={onNext}
          onBack={onBack}
          defaultData={step6}
          onComplete={onComplete}
          finalRequest={finalRequest}
          saveRequest={() => submitJob('save')}
        />
      )
    },
  ], [data, guid, step1, step2, step3, step4, step5, step6, completedEdit, submitJob]);

  useEffect(() => {
    if (data) {
      // editJob invalidates jobData. A successful save can therefore refetch while
      // the user has already made another edit. Preserve that live form and its
      // accepted-save baseline until the user saves it or opens another listing.
      if (loadedGuid.current === guid && savedSteps.current && draftSteps.current
        && (pendingLogoUploads.current > 0
          || hasEditJobChanges(draftSteps.current, savedSteps.current))) {
        return;
      }
      if (loadedGuid.current && loadedGuid.current !== guid) {
        resetActiveStep();
        setCompletedEdit(false);
      }
      loadedGuid.current = guid;
      const loadedStep2 = {
        ...data.stepper.step2,
        locations: formattedLocations(data.stepper.step2.locations)
      };
      // The Short Description is one line now; older listings can end in a line break.
      const loadedStep3 = {
        ...data.stepper.step3,
        shortDescription: data.stepper.step3.shortDescription
          && replaceLineBreaks(data.stepper.step3.shortDescription).trim()
      };
      let loadedStep5 = data.stepper.step5;

      if (data.stepper.step5.applicationEmail && !data.stepper.step5.tempAdditionalEmails) {
        const emails = data.stepper.step5.applicationEmail
          .split(';')
          .map((e) => e.trim())
          .filter(Boolean)
          .map((email) => ({ email }));
        loadedStep5 = {
          ...data.stepper.step5,
          showEmails: true,
          tempAdditionalEmails: emails
        };
      }
      const loadedSteps = {
        step1: data.stepper.step1,
        step2: loadedStep2,
        step3: loadedStep3,
        step4: data.stepper.step4,
        step5: loadedStep5,
      };
      // Form libraries may mutate nested field-array values. Preserve a separate
      // loaded snapshot so those writes cannot silently move the comparison base.
      savedSteps.current = JSON.parse(JSON.stringify(loadedSteps)) as IEditableJobSteps;
      draftSteps.current = loadedSteps;
      committedSteps.current = snapshot(loadedSteps);
      pendingLogoUploads.current = 0;
      setHasUnsavedChanges(false);
      setStep1(loadedSteps.step1);
      setStep2(loadedSteps.step2);
      setStep3(loadedSteps.step3);
      setStep4(loadedSteps.step4);
      setStep5(loadedSteps.step5);
      setStep6(data.stepper.step6);
    }
  }, [data, guid]);

  useEffect(() => {
    putSteps(steps);
  }, [putSteps, step1, step2, step3, step4, step5, step6, completedEdit]);

  if (!data) {
    return <Loader isShow />;
  }

  return (
    <div>
      <LeavePageGuard
        when={hasUnsavedChanges}
        message={UNSAVED_EDIT_MESSAGE}
      />
      {renderStepper}
    </div>
  );
};
